'use client';

import Script from 'next/script';
import { useEffect, useRef } from 'react';
import type { Intercom, IntercomClientWidgetProps } from './types';

// https://www.intercom.com/help/en/articles/180-localize-intercom-to-work-with-multiple-languages
//
const languageMap: { [key: string]: string } = {
  en: 'en-US',
  fa: 'fa-IR',
  es: 'es-ES',
  pt: 'pt-PT',
  ar: 'ar-SA',
  ru: 'ru-RU',
  zh: 'zh-CN',
  tr: 'tr-TR',
  de: 'de-DE',
};

const IntercomClientWidget = ({
  userIdentifier,
  userHash,
  data,
  appId,
  apiBase,
  locale,
}: IntercomClientWidgetProps) => {
  const isInitialized = useRef(false);

  useEffect(() => {
    // Prevent multiple initializations
    if (isInitialized.current) {
      return;
    }

    console.log('Initializing IntercomClientWidget ...');

    const initializeIntercom = () => {
      // Define Intercom function
      const intercom: Intercom = function (command, parameter?) {
        if (intercom.q) {
          intercom.q.push([command, parameter]);
        } else {
          intercom.q = [];
          intercom.q.push([command, parameter]);
        }
      };

      // Set up Intercom settings
      window.intercomSettings = {
        hide_default_launcher: true,
        api_base: apiBase,
        app_id: appId,
        user_id: userIdentifier ?? null,
        user_hash: userHash ?? null,
        email: data?.email ?? null,
        name: data?.username ?? data?.email ?? data?.firstName ?? null,
        custom_launcher_selector: '.intercom_support',
        language_override: languageMap[locale] ?? 'en-US',
      };

      window.Intercom = intercom;
      isInitialized.current = true;
    };

    // Initialize immediately
    initializeIntercom();
  }, [userIdentifier, userHash, data, locale, appId, apiBase]);

  const handleScriptLoad = () => {
    console.log('Head Tags :: Intercom script loaded successfully');
  };

  const handleScriptError = () => {
    console.error('Head Tags :: Failed to load Intercom script');
  };

  return (
    <Script
      src={`https://widget.intercom.io/widget/${appId}`}
      strategy="lazyOnload"
      onLoad={handleScriptLoad}
      onError={handleScriptError}
    />
  );
};

export default IntercomClientWidget;
